import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, STATI, PRIORITA } from "@/lib/api";
import { ReservedLine } from "@/components/client/ReservedLine";
import { CommentsThread } from "./CommentsThread";

const EMPTY = { titolo: "", descrizione: "", client_id: "", assignee_id: "none", scadenza: "", priorita: "media", stato: "da_fare" };

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>
);

const Pick = ({ value, onChange, options, testid, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="rounded-xl"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const TaskFormDialog = ({ open, onOpenChange, task, clientId, users = [], clients = [], onSaved, onCommentsChange }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(task ? { ...EMPTY, ...task, assignee_id: task.assignee_id || "none", scadenza: task.scadenza || "" }
              : { ...EMPTY, client_id: clientId || "" });
  }, [open, task, clientId]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.titolo.trim()) return toast.error("Inserisci un titolo");
    if (!f.client_id) return toast.error("Seleziona un cliente");
    setSaving(true);
    const body = { titolo: f.titolo, descrizione: f.descrizione, client_id: f.client_id, priorita: f.priorita, stato: f.stato,
      assignee_id: f.assignee_id === "none" ? null : f.assignee_id, scadenza: f.scadenza || null };
    try {
      const r = task ? await api.put(`/tasks/${task.id}`, body) : await api.post("/tasks", body);
      toast.success(task ? "Task aggiornata" : "Task creata");
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const userOpts = [{ value: "none", label: "Nessuno" }, ...users.filter((u) => u.is_active || u.id === task?.assignee_id)
    .map((u) => ({ value: u.id, label: `${u.nome} ${u.cognome}` }))];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[94vh] overflow-y-auto rounded-2xl core-scroll" data-testid="task-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black text-[#0A1128]">{task ? "Modifica task" : "Nuova task"}</DialogTitle>
          {f.client_id && <ReservedLine clientId={f.client_id} testid="task-form-reserved" />}</DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Titolo *"><Input value={f.titolo} onChange={set("titolo")} className="rounded-xl" data-testid="task-form-titolo" /></Field>
          <Field label="Descrizione"><Textarea rows={3} value={f.descrizione} onChange={set("descrizione")} className="rounded-xl" data-testid="task-form-descrizione" /></Field>
          {!clientId && (
            <Field label="Cliente *">
              <Pick value={f.client_id} onChange={set("client_id")} testid="task-form-cliente" placeholder="Seleziona cliente"
                options={clients.map((c) => ({ value: c.id, label: c.ragione_sociale }))} />
            </Field>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Socio assegnato"><Pick value={f.assignee_id} onChange={set("assignee_id")} testid="task-form-socio" options={userOpts} /></Field>
            <Field label="Scadenza"><Input type="date" value={f.scadenza} onChange={set("scadenza")} className="rounded-xl" data-testid="task-form-scadenza" /></Field>
            <Field label="Priorità"><Pick value={f.priorita} onChange={set("priorita")} testid="task-form-priorita" options={PRIORITA} /></Field>
            <Field label="Stato"><Pick value={f.stato} onChange={set("stato")} testid="task-form-stato" options={STATI} /></Field>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="task-form-cancel">Annulla</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] px-6" data-testid="task-form-submit">
              {saving ? "Salvataggio..." : task ? "Salva modifiche" : "Crea task"}
            </Button>
          </DialogFooter>
        </form>
        {task && <CommentsThread tipo="task" targetId={task.id} onChange={onCommentsChange} />}
      </DialogContent>
    </Dialog>
  );
};
